import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { PreLoginResponse } from '../api';
import { preLogin } from '../api';

type UseInviteCheckResult = {
    cachedPreLoginResponse: PreLoginResponse | null;
    displayInviteInfoPanel: boolean;
    isCheckingInvite: boolean;
    checkInvite: (email: string) => Promise<void>;
};

export const useInviteCheck = (inviteType: string): UseInviteCheckResult => {
    const [isCheckingInvite, setIsCheckingInvite] = useState(false);
    const [cachedPreLoginResponse, setCachedPreLoginResponse] = useState<PreLoginResponse | null>(null);
    // track the key (inviteType:email) for which the cached response is valid or in-flight
    const cachedKeyRef = useRef<string | null>(null);

    // Reset cache when inviteType changes
    useEffect(() => {
        cachedKeyRef.current = null;
        setCachedPreLoginResponse(null);
    }, [inviteType]);

    const checkInvite = useCallback(
        async (email: string) => {
            if (!inviteType || !email) {
                return;
            }

            const key = `${inviteType}:${email}`;

            // If we already have a cached response for this exact key, skip
            if (cachedPreLoginResponse && cachedKeyRef.current === key) {
                return;
            }

            setIsCheckingInvite(true);
            // mark this key as in-flight/desired
            cachedKeyRef.current = key;

            try {
                const res = await preLogin(email);
                // only commit the response if the key still matches (guards out-of-order responses)
                if (cachedKeyRef.current === key) {
                    setCachedPreLoginResponse(res);
                }
            } catch (err) {
                console.error('PreLogin check failed:', err);
            } finally {
                setIsCheckingInvite(false);
            }
        },
        [inviteType, cachedPreLoginResponse]
    );

    const displayInviteInfoPanel = useMemo(
        () => Boolean(inviteType && cachedPreLoginResponse?.userFound),
        [inviteType, cachedPreLoginResponse]
    );

    return {
        cachedPreLoginResponse,
        displayInviteInfoPanel,
        isCheckingInvite,
        checkInvite
    };
};
